import { LockKeyhole, Plus, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MAX_CATALOGUE_LANGUAGES,
  SUPPORTED_LANGUAGES,
  canonicalLanguages,
  type LanguageCode,
} from "@/constants/languages";
import { cn } from "@/lib/utils";

interface LanguageSelectorProps {
  value: LanguageCode[];
  onChange: (languages: LanguageCode[]) => void;
  disabled?: boolean;
  className?: string;
  idPrefix?: string;
}

export function LanguageSelector({
  value,
  onChange,
  disabled = false,
  className,
  idPrefix = "catalogue-language",
}: LanguageSelectorProps) {
  const selected = canonicalLanguages(value);
  const atLimit = selected.length >= MAX_CATALOGUE_LANGUAGES;
  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    ({ code }) => !selected.includes(code)
  );

  const addLanguage = (code: LanguageCode) => {
    if (atLimit || selected.includes(code)) return;
    onChange(canonicalLanguages([...selected, code]));
  };

  const removeLanguage = (code: LanguageCode) => {
    if (code === "en") return;
    onChange(selected.filter((language) => language !== code));
  };

  const helperId = `${idPrefix}-help`;

  return (
    <fieldset
      className={cn("min-w-0 space-y-3", className)}
      disabled={disabled}
      aria-describedby={helperId}
    >
      <legend className="text-sm font-medium text-foreground">Enrichment languages</legend>
      <div className="flex items-start justify-between gap-4">
        <p id={helperId} className="text-xs leading-5 text-muted-foreground">
          Choose up to five languages. English is always included.
        </p>
        <span className="shrink-0 text-xs text-muted-foreground" aria-live="polite">
          {selected.length}/{MAX_CATALOGUE_LANGUAGES} selected
        </span>
      </div>

      <div
        className={cn(
          "flex min-h-12 min-w-0 flex-wrap items-center gap-2 rounded-lg border bg-background p-2 transition-shadow focus-within:border-pink-300 focus-within:ring-2 focus-within:ring-pink-100 dark:focus-within:border-pink-800 dark:focus-within:ring-pink-950",
          disabled && "bg-muted/30"
        )}
      >
        {selected.map((code) => {
          const language = SUPPORTED_LANGUAGES.find((option) => option.code === code);
          if (!language) return null;

          const required = code === "en";

          return (
            <span
              key={code}
              className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border border-pink-200 bg-pink-50 px-3 text-sm text-pink-700 dark:border-pink-900 dark:bg-pink-950/40 dark:text-pink-300"
              title={required ? "English is required" : undefined}
            >
              <span className="truncate">{language.name}</span>
              <span className="text-[10px] font-semibold uppercase opacity-60">{code}</span>
              {required ? (
                <>
                  <LockKeyhole aria-hidden="true" className="h-3 w-3 shrink-0 opacity-60" />
                  <span className="sr-only">Required</span>
                </>
              ) : disabled ? null : (
                <button
                  type="button"
                  onClick={() => removeLanguage(code)}
                  className="-mr-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-pink-600 transition-colors hover:bg-pink-100 hover:text-pink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 dark:text-pink-300 dark:hover:bg-pink-900/60"
                  aria-label={`Remove ${language.name}`}
                >
                  <X aria-hidden="true" className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}

        {!disabled && !atLimit && availableLanguages.length > 0 ? (
          <Select value="" onValueChange={(code) => addLanguage(code as LanguageCode)}>
            <SelectTrigger
              id={`${idPrefix}-add`}
              aria-label="Add enrichment language"
              className="relative h-8 w-auto min-w-36 rounded-full border-dashed bg-transparent pl-8 pr-2.5 text-xs shadow-none focus:ring-0 focus:ring-offset-0"
            >
              <Plus
                aria-hidden="true"
                className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
              />
              <SelectValue placeholder="Add language" />
            </SelectTrigger>
            <SelectContent>
              {availableLanguages.map(({ code, name }) => (
                <SelectItem key={code} value={code}>
                  <span className="flex items-center gap-2">
                    <span>{name}</span>
                    <span className="text-xs uppercase text-muted-foreground">{code}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        {!disabled && atLimit ? (
          <span className="px-1 text-xs text-muted-foreground">Maximum selected</span>
        ) : null}
      </div>
    </fieldset>
  );
}
